import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import FolderRoundedIcon                 from "@mui/icons-material/FolderRounded";
import ExpandLessRoundedIcon             from "@mui/icons-material/ExpandLessRounded";
import SubdirectoryArrowRightRoundedIcon from "@mui/icons-material/SubdirectoryArrowRightRounded";
import { ds } from "../../styles/designSystem";
import { buildFormTree, folderColor } from "./formTree";

/**
 * 양식 폴더 트리 — 상신 모달과 관리자 화면이 같이 쓴다.
 *
 * 메일 폴더 사이드바와 같은 모양이다: 폴더 아이콘 + 접기 화살표, 하위는 «↳» 로 이어 붙인다.
 * 양식마다 아이콘을 주지 않는 이유는 19개가 제각각 색을 갖는 순간 이름이 아니라 색이 먼저 읽히기
 * 때문이다 — 여기서 고르는 기준은 «무슨 양식인가» 하나뿐이다.
 *
 * @param groups   [{groupCode, groupName, forms:[…]}]
 * @param keyOf    양식 → 고유 키 (선택 비교용)
 * @param selected 지금 선택된 키
 * @param onSelect 양식 클릭
 * @param query    검색어 — 있으면 접힘을 무시한다
 * @param renderRight 양식 줄 오른쪽에 붙일 것(배지 등)
 * @param focusGroup {@code {code, seq}} — 그 최상위 그룹으로 데려간다. 관리자 사이드바가 접힘
 *                   상태에서 그룹을 골랐을 때만 쓴다. 상신 모달은 넘기지 않는다(기본 동작 그대로).
 *                   {@code seq} 는 같은 그룹을 다시 골랐을 때도 다시 반응하게 하는 값이다.
 */
export default function FormTreeNav({
    groups, keyOf, selected, onSelect, query = "", renderRight, focusGroup,
}) {
    const tree = useMemo(() => buildFormTree(groups), [groups]);
    const [collapsed, setCollapsed] = useState(() => new Set());

    /** 그룹 코드 → 그 그룹의 DOM 노드. focusGroup 이 왔을 때 그 자리로 스크롤하는 데만 쓴다. */
    const groupRefs = useRef(new Map());

    useEffect(() => {
        const code = focusGroup?.code;
        if (!code) return;
        const gid = `g:${code}`;
        // 접혀 있으면 편다 — 그 그룹으로 데려갔는데 폴더가 닫힌 채면 이동한 티가 나지 않는다.
        setCollapsed((prev) => {
            if (!prev.has(gid)) return prev;
            const next = new Set(prev);
            next.delete(gid);
            return next;
        });
        // "nearest" — 이미 보이는 그룹이면 화면을 흔들지 않는다.
        groupRefs.current.get(code)?.scrollIntoView({ block: "nearest" });
    }, [focusGroup]);

    const searching = Boolean(query.trim());
    const isOpen = (id) => searching || !collapsed.has(id);
    const toggle = (id) => setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    return (
        <>
            {tree.map((g) => {
                const gid = `g:${g.groupCode}`;
                const total = g.nodes.reduce(
                    (n, x) => n + (x.kind === "folder" ? x.forms.length : 1), 0);
                return (
                    <Box
                        key={gid}
                        ref={(el) => {
                            if (el) groupRefs.current.set(g.groupCode, el);
                            else groupRefs.current.delete(g.groupCode);   // 검색으로 사라진 그룹을 남겨 두지 않는다
                        }}
                        sx={{ mb: 0.25 }}
                    >
                        <FolderRow
                            name={g.groupName} count={total} depth={0}
                            open={isOpen(gid)} onToggle={() => toggle(gid)}
                        />
                        <Collapse in={isOpen(gid)}>
                            {g.nodes.map((node) => {
                                if (node.kind === "form") {
                                    return (
                                        <FormRow
                                            key={keyOf(node.form)} depth={1}
                                            label={node.label}
                                            form={node.form}
                                            active={keyOf(node.form) === selected}
                                            onClick={() => onSelect(node.form)}
                                            renderRight={renderRight}
                                        />
                                    );
                                }
                                const fid = `${gid}/f:${node.name}`;
                                return (
                                    <Box key={fid}>
                                        <FolderRow
                                            name={node.name} count={node.forms.length} depth={1}
                                            open={isOpen(fid)} onToggle={() => toggle(fid)}
                                        />
                                        <Collapse in={isOpen(fid)}>
                                            {node.forms.map(({ form, label }) => (
                                                <FormRow
                                                    key={keyOf(form)} depth={2}
                                                    label={label}
                                                    form={form}
                                                    active={keyOf(form) === selected}
                                                    onClick={() => onSelect(form)}
                                                    renderRight={renderRight}
                                                />
                                            ))}
                                        </Collapse>
                                    </Box>
                                );
                            })}
                        </Collapse>
                    </Box>
                );
            })}
        </>
    );
}

/**
 * 폴더 한 줄 — 그룹(depth 0)과 하위 폴더(depth 1)가 같은 컴포넌트를 쓴다.
 *
 * 굵게 쓰는 건 폴더뿐이다. 양식 줄까지 굵으면 어디가 묶음이고 어디가 항목인지 구분이 사라진다.
 */
function FolderRow({ name, count, depth, open, onToggle }) {
    const child = depth > 0;
    return (
        <Stack
            direction="row" alignItems="center" spacing={child ? 0.6 : 1.1}
            onClick={onToggle}
            sx={{
                pl: 1.1 + depth * 1.4, pr: 1.1, py: child ? 0.65 : 0.9,
                mx: 0.3, borderRadius: 1.2, cursor: "pointer",
                "&:hover": { bgcolor: "#f1f5f9" },
            }}>
            {child && (
                <SubdirectoryArrowRightRoundedIcon
                    sx={{ fontSize: 14, color: "#cbd5e1", ml: -0.2, flexShrink: 0 }} />
            )}
            <FolderRoundedIcon
                sx={{ fontSize: child ? 16 : 18, color: folderColor(name, depth), flexShrink: 0 }} />
            <Typography noWrap sx={{
                flex: 1, minWidth: 0,
                fontSize: child ? 13 : 13.5,
                fontWeight: 800,
                color: "#0f172a",
            }}>
                {name}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{count}</Typography>
            <IconButton
                size="small" onClick={(e) => { e.stopPropagation(); onToggle(); }}
                sx={{ p: 0, color: "#94a3b8", "&:hover": { color: ds.colors.primary, bgcolor: "transparent" } }}>
                <ExpandLessRoundedIcon sx={{
                    fontSize: 16, transition: "transform 160ms ease",
                    transform: open ? "none" : "rotate(180deg)",
                }} />
            </IconButton>
        </Stack>
    );
}

/** 양식 한 줄 — 아이콘 없이 «↳» 와 이름만. 폴더와 달리 보통 굵기로 둔다. */
function FormRow({ label, form, depth, active, onClick, renderRight }) {
    return (
        <Stack
            direction="row" alignItems="flex-start" spacing={0.5}
            onClick={onClick}
            sx={{
                pl: 1.1 + depth * 1.4, pr: 1.1, py: 0.55, mx: 0.3, borderRadius: 1.2,
                cursor: "pointer",
                bgcolor: active ? ds.colors.primaryBg : "transparent",
                "&:hover": { bgcolor: active ? ds.colors.primaryBg : "#f1f5f9" },
            }}>
            <SubdirectoryArrowRightRoundedIcon
                sx={{ fontSize: 14, ml: -0.2, mt: 0.25, flexShrink: 0,
                      color: active ? ds.colors.primarySoft : "#cbd5e1" }} />
            {/* 줄바꿈을 허용한다 — «[매출]세금계산서 발행 요청건 (지출결의서로 올려주세요)» 처럼
                긴 이름이 잘리면 괄호 안(고르는 기준·안내)이 통째로 사라진다. */}
            <Typography sx={{
                flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.4,
                fontWeight: active ? 700 : 400,
                color: form.active === false ? "#94a3b8"
                     : active ? ds.colors.primaryDark : "#334155",
            }}>
                {label}
            </Typography>
            {renderRight?.(form)}
        </Stack>
    );
}
